import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Icons from '@mui/icons-material';
import { createClasses } from '@kp/react-ui';
import { Grid, Button, IconButton, Tooltip } from '@mui/material';
import { FancyCard } from '../../../components/FancyCard';
import { InfoList } from '../../../components/InfoList';
import { InfoListItem } from '../../../components/InfoListItem';
import { ActionsBar } from '../../../components/ActionsBar';
import { DataTypes } from '../../../__generated__/types';
import { DataTypeLabel } from '../../../components/DataTypeLabel';

const classes = createClasses({
  description: {
    whiteSpace: 'pre-wrap',
  },
  tooltipContent: {
    padding: '8px',
    fontSize: '14px',
    lineHeight: '16px',
  },
});

type AttributeDetailsProps = {
  name: string;
  description: string;
  dataType: DataTypes | null;
  required: boolean;
  isGlobal: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  loading?: boolean;
};

export const AttributeDetails: React.FC<AttributeDetailsProps> = ({
  name,
  description,
  dataType,
  required,
  isGlobal,
  onEdit,
  onDelete,
  loading,
}) => {
  const { t } = useTranslation(['attributes', 'general']);

  return (
    <>
      {!isGlobal && onDelete && (
        <ActionsBar>
          {!loading && (
            <Button color="primary" size="large" onClick={onDelete}>
              {t('attributes:details.actions.delete')}
            </Button>
          )}
        </ActionsBar>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={3} alignItems="stretch" direction="row">
            <Grid item xs={12} sm={12} md={8}>
              <FancyCard
                title={t('attributes:details.info.title')}
                actions={
                  onEdit && (
                    <Tooltip
                      arrow
                      placement="top-end"
                      title={
                        isGlobal
                          ? t('attributes:details.globalWarning') ?? ''
                          : ''
                      }
                      aria-label="edit-tooltip"
                    >
                      <span>
                        <IconButton
                          aria-label="edit"
                          data-testid="card-edit"
                          onClick={onEdit}
                          size="medium"
                          color="primary"
                          disabled={isGlobal}
                        >
                          <Icons.Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                }
                loading={loading}
                fullHeight
              >
                <InfoList>
                  <InfoListItem term={t('general:fields.name')}>
                    {name}
                  </InfoListItem>
                  <InfoListItem
                    className={classes.description}
                    term={t('general:fields.description')}
                  >
                    {description}
                  </InfoListItem>
                </InfoList>
              </FancyCard>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <FancyCard
                title={t('attributes:details.settings.title')}
                loading={loading}
                fullHeight
                actions={
                  onEdit && (
                    <Tooltip
                      arrow
                      placement="top-end"
                      title={
                        <div className={classes.tooltipContent}>
                          {t('attributes:details.settings.disable')}
                        </div>
                      }
                      aria-label="non-editable-tooltip"
                    >
                      <span>
                        <IconButton
                          aria-label="edit"
                          onClick={onEdit}
                          size="medium"
                          color="primary"
                          disabled
                        >
                          <Icons.Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                }
              >
                <InfoList>
                  <InfoListItem
                    term={t('attributes:details.settings.dataType')}
                  >
                    <DataTypeLabel dataType={dataType} />
                  </InfoListItem>
                  <InfoListItem term={t('general:forms.required')}>
                    {required
                      ? t('attributes:details.requiredYes')
                      : t('attributes:details.requiredNo')}
                  </InfoListItem>
                </InfoList>
              </FancyCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
